import { Injectable, inject } from '@angular/core';
import {
  CoreHttpClient,
  CoreLogin,
  CoreMobiResource,
  CoreMobiResourceType,
  selectorCoreInstanceInfo,
  selectorCoreLogin
} from '@bepatient/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { TimelineApiService } from './timeline-api.service';
import { Timeline, TimelineInfoMessage } from './timeline.models';

/**
 * Service responsible for SMART API calls for the timeline
 */
@Injectable()
export class TimelineApiSmartService implements TimelineApiService {
  private http = inject(CoreHttpClient);
  private store = inject<Store<any>>(Store);
  private translate = inject(TranslateService);

  /**
   * Wrapper to load the timeline via [realTimeline]{@link #realTimeline}
   */
  loadTimeline(): Observable<Timeline> {
    return this.realTimeline();
  }

  /**
   * DO a POST to change the status of an info message to "done"
   *
   * `POST /rest/contents/site-infos/[uuid]{@link TimelineInfoMessage#uuid}`
   *
   * @param infoMessage The info message to be changed to "done"
   */
  infoMessageDone(infoMessage: TimelineInfoMessage): Observable<any> {
    return of({});
    // this.http.post(
    //   `/rest/contents/site-infos/${infoMessage.uuid}`,
    //   {},
    //   {
    //     withMobiCredentials: true
    //   }
    // );
  }

  /**
   * Load the timeline and parse the data returned by SMART
   *
   * `GET /rest/timeline`
   */
  realTimeline(): Observable<Timeline> {
    return this.http
      .get<any>('/rest/timeline', {
        withMobiCredentials: true
      })
      .pipe(
        mergeMap<any, any[]>(res =>
          res.payload.days.map(day => [
            day.activities,
            res.payload.info_messages,
            res.payload.rrules_info || []
          ])
        ),
        withLatestFrom(
          this.store.pipe(select(selectorCoreInstanceInfo)),
          this.store.pipe(select(selectorCoreLogin)),
          (items, instanceInfo, login) => [
            items,
            (instanceInfo && instanceInfo.appColor) || '',
            login
          ]
        ),
        map<[any[], string, CoreLogin], Timeline>(([data, appColor, login]) => {
          const items = data[0];
          const infoMessages = data[1];
          const rrules = data[2];
          const lang = login.language;
          moment.locale(lang);
          const timeline: Timeline = {
            days: [
              {
                date: moment().format('ddd D MMMM'),
                subDate: 'TIMELINE.TODAY',
                activities: []
              },
              {
                date: moment().add(-1, 'days').format('ddd D MMMM'),
                subDate: 'TIMELINE.YESTERDAY',
                activities: []
              },
              {
                date: 'TIMELINE.TWO_DAYS',
                subDate: 'TIMELINE.OR_MORE',
                activities: []
              }
            ],
            infoMessages: [],
            rrules_info: []
          };

          const convertResource = (
            type: string,
            href: string
          ): CoreMobiResource | any => {
            switch (type) {
              case 'activity-form':
                return { type: CoreMobiResourceType.FORM, href };
              case 'activity-category':
                return { type: CoreMobiResourceType.CATEGORY, href };
              case 'activity-forms-container':
                return { type: CoreMobiResourceType.CATEGORY, href };
              case 'activity-factsheet':
                return { type: CoreMobiResourceType.FACTSHEET, href };
              case 'activity-quizz':
                return { type: CoreMobiResourceType.QUIZZ, href };
              case 'activity-chatbot':
                return { type: CoreMobiResourceType.CHATBOT, href };
              case 'activity-body-pain':
                return { type: CoreMobiResourceType.BODY_SCHEMA, href };
              case 'activity-form-webview':
                return { type: CoreMobiResourceType.DEEPLINK, href };
              case 'activity-close':
                return { type: type, href };
              default:
                return void 0;
            }
          };

          rrules.forEach((item, index) => {
            // Normalize valid_dates by adding seconds if missing
            const normalizedValidDates =
              item.valid_dates?.map(dateArr => {
                // Check if date string has seconds (format: YYYY-MM-DD HH:MM:SS)
                // If it only has HH:MM, add :00 for seconds
                if (
                  dateArr &&
                  dateArr[0] &&
                  dateArr[0].match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/)
                ) {
                  dateArr[0] = dateArr[0] + ':00';
                  let status = dateArr?.[1] ?? '';
                  return [dateArr[0], status];
                }
                return dateArr;
              }) || [];

            let newOccurrence =
              items.filter(activity => activity.id === item.id)[0].occurrence ??
              1;
            timeline.rrules_info.push({
              ...item,
              occurrence: newOccurrence,
              valid_dates: normalizedValidDates
            });
          });

          // Parse timeline days
          items.forEach(item => {
            item.created_at = moment();
            const daysAgo = moment().diff(
              moment(item.created_at).startOf('day'),
              'days'
            );

            timeline.days[daysAgo > 2 ? 2 : daysAgo].activities.push({
              uuid: item.id,
              color: item.color || appColor,
              title: item.title || '',
              image: item.image || '',
              icon: item.icon || '',
              message: item.description || '',
              blocking: item.blocking || 0,
              progress: item.progress / 100 || 0,
              occurrence: item.occurrence || 0,
              resource: convertResource(item.type || '', item.href || ''),
              rrules:
                timeline.rrules_info.filter(rrule => rrule.id === item.id)[0] ??
                undefined
            });
          });

          // Parse info messages
          infoMessages.forEach(item => {
            timeline.infoMessages.push({
              uuid: item.id || '',
              href: item.href || '',
              color: item.color || appColor,
              content: item.message || '',
              icon: item.icon || '',
              buttonLabel: item.button_label,
              blocking: item.blocking || 0,
              showButton: item.show_button,
              // SMARTAPP-99 - Using CoreMobiResourceType for Info Message type,
              // If type not received setting it as activity-close
              resource: convertResource(
                item.type || 'activity-close',
                item.href || ''
              )
            });
          });

          return {
            ...timeline,
            days: timeline.days.filter(day => day.activities.length)
          };
        })
      );
  }

  /**
   * DO a PUT to save the form answers
   *
   * `PUT /rest/contents/[formId]/answers/[formId]`
   * @param formId The form ID
   * @param data The form data
   */
  saveForm(formId: string, data: any): Observable<any> {
    return this.http
      .put(`/rest/contents/forms/${formId}/answers/${formId}`, data, {
        withMobiCredentials: true
      })
      .pipe(
        map((response: any) => {
          const payload = response.payload;
          return payload;
        }),
        catchError(err =>
          throwError(() => (err || err.error || err.error.messages) ?? void 0)
        )
      );
  }
}
